/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * @emails react-core
 * @flow
 */

 import Container from 'components/Container';
 import Flex from 'components/Flex';
 import MarkdownHeader from 'components/MarkdownHeader';
 import NavigationFooter from 'templates/components/NavigationFooter';
 // $FlowFixMe Update Flow
 import React from 'react';
 import StickyResponsiveSidebar from 'components/StickyResponsiveSidebar';
 import TitleAndMetaTags from 'components/TitleAndMetaTags';
 import FeedbackForm from 'components/FeedbackForm';
 import findSectionForPath from 'utils/findSectionForPath';
 import toCommaSeparatedList from 'utils/toCommaSeparatedList';
 import {sharedStyles} from 'theme';
 import createCanonicalUrl from 'utils/createCanonicalUrl';
 import {colors, media} from 'theme';
 import { Disqus } from 'gatsby-plugin-disqus';
 import {Link} from 'gatsby';
 import Sidebar from "../../templates/components/Sidebar";
 import {createLinkDocs} from 'utils/createLink';
 import {sectionListDocs} from '../../utils/sectionList';
 import type {Node} from 'types';
 import { url } from "site";
 
 type Props = {
   authors: Array<string>,
   createLink: Function, // TODO: Add better flow type once we Flow-type createLink
   date?: string,
   enableScrollSync?: boolean,
   ogDescription: string,
   location: Location,
   markdownRemark: Node,
   sectionList: Array<Object>, // TODO: Add better flow type once we have the Section component
   titlePostfix: string,
 };
 
 const getPageById = (sectionList: Array<Object>, templateFile: ?string) => {
   if (!templateFile) {
     return null;
   }
 
   const sectionItems = sectionList.map(section => section.items);
   const flattenedSectionItems = [].concat.apply([], sectionItems);
   const linkId = templateFile.replace('.html', '');
   return flattenedSectionItems.find(item => item.title === linkId);
 };
 const linkCss = {
   color: colors.text,
   display: 'inline-block',
   borderBottom: '1px solid transparent',
   transition: 'border 0.2s ease',
   marginTop: 5,
   backgroundColor: "rgba(187,239,253,0.3)",
   borderBottom: "1px solid rgba(0,0,0,0.2)",
   color: "#1a1a1a",
   '&:hover': {
     color: colors.subtle,
   },
 };
 const MarkdownPage = ({
   authors = [],
   createLink,
   date,
   enableScrollSync,
   ogDescription,
   location,
   markdownRemark,
   sectionList,
   titlePostfix = '',
 }: Props) => {
   const hasAuthors = authors&&authors.length > 0;
   const titlePrefix = markdownRemark.frontmatter.title || '';
   const prev = getPageById(sectionList, markdownRemark.frontmatter.prev);
   const next = getPageById(sectionList, markdownRemark.frontmatter.next);
   return (
     <Flex
       direction="column"
       grow="1"
       shrink="0"
       halign="stretch"
       css={{
         width: '100%',
         flex: '1 0 auto',
         position: 'relative',
         zIndex: 0,
         '& h1, & h2, & h3, & h4, & h5, & h6': {
           scrollMarginTop: 'var(--banner-height-normal)',
           [media.lessThan('small')]: {
             scrollMarginTop: 'var(--banner-height-small)',
           },
         },
       }}>
       <TitleAndMetaTags
         ogDescription={ogDescription}
         ogType="article"
         canonicalUrl={createCanonicalUrl(markdownRemark.fields.slug)}
         title={`${titlePrefix}${titlePostfix}`}
       />
       <div css={{flex: '1 0 auto'}}>
         <Container>
           <div css={sharedStyles.articleLayout.container}>
             <Flex type="article" direction="column" grow="1" halign="stretch">
               <MarkdownHeader title={titlePrefix} />
 
               {(date || hasAuthors) && (
                 <div css={{marginTop: 15}}>
                   {date}{' '}
                   {hasAuthors && (
                     <span>
                       by{' '}
                       {toCommaSeparatedList(authors, author => (
                         <a
                           css={sharedStyles.link}
                           href={author.frontmatter.url}
                           key={author.frontmatter.name}>
                           {author.frontmatter.name}
                         </a>
                       ))}
                     </span>
                   )}
                 </div>
               )}
 
               <div css={sharedStyles.articleLayout.content} className={markdownRemark.fields.path.includes("docs/getting-started")?"docs-markdown":""}>
                 <div
                   css={[sharedStyles.markdown]}
                   dangerouslySetInnerHTML={{__html: markdownRemark.html}}
                 />
                 {markdownRemark.fields.path.includes("docs/getting-started")?
                   <Sidebar
                   createLink={createLinkDocs}
                   defaultActiveSection={findSectionForPath(
                     location.pathname,
                     sectionListDocs,
                   )}
                   location={location}
                   sectionList={sectionListDocs}
                   title={""}
                 />
                 :''}
                 {markdownRemark.fields.path && markdownRemark.fields.references &&markdownRemark.fields.references.length>0?(
                   <div css={{margin: "10px 0"}}>
                     <h4>المصادر</h4>
                     {
                       markdownRemark.fields.references.map((r,i)=>(
                         <span>
                         <Link
                         css={[linkCss]}
                         to={r.refLink}>
                         {r.refTitle}<sup>[{i+1}]</sup></Link>&nbsp;&nbsp;</span>
                       ))
                     }
                   </div>
                 ):''}
                 {markdownRemark.fields.path && (
                   <div css={{margin: "10px 0"}}>
                     <a
                       onClick={()=>{
                         const container = document.querySelector("#feedback-modal-container")
                         container.classList.add('one');
                         container.classList.remove("out");
                         document.querySelector("body").classList.add('modal-active');
                       }}
                       css={sharedStyles.articleLayout.editLink}
                       href="#">
                       تحرير هذه الصفحة
                     </a>
                     <FeedbackForm pagrUrl={location.href} />
                   </div>
                 )}
                 {(next || prev) && (
                   <NavigationFooter location={location} next={next} prev={prev} />
                 )}
                 {/* <div className="fb-comments" data-href={url+location.pathname} data-width="" data-numposts="5"></div> */}
                 <Disqus
                   config={{
                       /* Replace PAGE_URL with your post's canonical URL variable */
                       url: location.href,
                       /* Replace PAGE_IDENTIFIER with your page's unique identifier variable */
                       identifier: location.pathname,
                       /* Replace PAGE_TITLE with the title of the page */
                       title: markdownRemark.frontmatter.title,
                   }}
               />
               </div>
             </Flex>
 
             <div css={sharedStyles.articleLayout.sidebar}>
               <StickyResponsiveSidebar
                 enableScrollSync={enableScrollSync}
                 createLink={createLink}
                 defaultActiveSection={findSectionForPath(
                   location.pathname,
                   sectionList,
                 )}
                 location={location}
                 sectionList={sectionList}
               />
             </div>
           </div>
         </Container>
       </div>
       <div id="feedback-modal-container">
         <div class="modal-background" onClick={(e)=>{
           if(e&&e.target&&e.target.className&&e.target.className.indexOf&&e.target.className.indexOf("modal-background")>-1){
             const container = document.querySelector("#feedback-modal-container")
             container.classList.add("out");
             document.querySelector("body").classList.remove('modal-active');
           }
       }}>
           <div class="modal">
             <div className="modal-content" style={{"position": "relative","zIndex":3}}>
               <button onClick={(e)=>{
             const container = document.querySelector("#feedback-modal-container")
             container.classList.add("out");
             document.querySelector("body").classList.remove('modal-active');
       }} style={{
     position: "absolute",
     top: "12px",
     right: "-23px",
     zIndex: 11,
     padding: "5px 10px"}}>اغلاق</button>
               <iframe class="airtable-embed" src={`https://airtable.com/embed/shrmBFxZPG3DQlIyE?backgroundColor=pink&prefill_url=https://zamalekwiki.com${location.pathname}`} frameborder="0" onmousewheel="" width="100%" style={{background: "transparent", border: "1px solid #ccc", height: "80vh"}}></iframe>
             </div>
             <svg class="modal-svg" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" preserveAspectRatio="none">
                       <rect x="0" y="0" fill="none" width="226" height="162" rx="3" ry="3"></rect>
                     </svg>
           </div>
         </div>
       </div>
     </Flex>
   );
 };
 
 export default MarkdownPage;
 